import React, { useCallback } from "react";
import { ButtonGroup, Dropdown } from "react-bootstrap";
import DropdownMenu from "react-bootstrap/esm/DropdownMenu";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { formatCurrency } from "../../lib/currency/currency";
import { Icon } from "../../components";
import { getSystemLocale } from "../../lib/locale/locale";
import { useAnalytics } from "../../lib/analytics/analytics";
import { calculateCreditsSavingPercent } from "../utils/calculate_credit_saving_percent";

export interface CreditOption {
  id: string;
  offeringId: string;
  displayName: string;
  numberOfCredits: number;
  price: number;
}

interface CreditQuantityPickerProps {
  options: CreditOption[];
  value?: string;
  onChange: (optionId: string, offeringId: string) => void;
  currency?: string | null;
}

export function CreditQuantityPicker({
  options,
  value,
  onChange,
  currency,
}: CreditQuantityPickerProps) {
  const { fbt } = useI18n();
  const analytics = useAnalytics();

  const optionsWithSavingPercent = calculateCreditsSavingPercent(options);
  const selectedOption = options.find((o) => o.id === value);
  const placeholder = selectedOption ? (
    selectedOption.displayName
  ) : (
    <span className="fw-lighter checkout-box-picker-selected__placeholder">
      {fbt("Select quantity")}
    </span>
  );

  const handleSelect = useCallback(
    (id: string | null) => {
      const option = options.find((o) => o.id === id);
      if (id && option) {
        onChange(id, option.offeringId);
        analytics.event(
          "Quantity Select Click",
          {
            Quantity: option?.numberOfCredits,
            Source: "WL",
          },
          { onlyProviders: ["mixpanel", "customerio"] }
        );
      }
    },
    [analytics, onChange, options]
  );

  return (
    <div className="checkout-box-picker-view">
      <Dropdown
        as={ButtonGroup}
        className={`checkout-box-picker row`}
        size="lg"
        onSelect={handleSelect}
      >
        <Dropdown.Toggle>
          <div className="row">
            <div className="col-10 checkout-box-picker-placeholder">
              <span className="row fw-bold text-uppercase checkout-box-picker-placeholder__label">
                {fbt("Quantity")}
              </span>
              <span className="row checkout-box-picker-selected">
                {placeholder}
              </span>
            </div>
            <div className="checkout-box-picker-icon">
              <Icon name="fi-chevron-down" size={24} />
            </div>
          </div>
        </Dropdown.Toggle>
        <DropdownMenu
          popperConfig={{
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  altAxis: true,
                },
              },
            ],
          }}
        >
          <Dropdown.Header>
            <div className="dropdown-header">
              <span className="row fw-bold text-uppercase">
                {fbt("Select quantity")}
              </span>
            </div>
          </Dropdown.Header>
          {optionsWithSavingPercent.map((o, index) => (
            <>
              <Dropdown.Item
                as="button"
                eventKey={o.id}
                active={o.id === value}
              >
                <div className="d-flex quantity-picker-option px-1">
                  <span className="col-6 align-self-start name">
                    {o.displayName}
                  </span>
                  <span className="col-3 align-self-center">
                    {o.savingPercent && o.savingPercent > 0 && (
                      <span className="badge bg-warning text-dark">
                        {`${fbt("Save")} ${o.savingPercent}%`}
                      </span>
                    )}
                  </span>
                  <span className="col-3 align-self-center price-view">
                    {formatCurrency(
                      o.price,
                      currency || "USD",
                      getSystemLocale()
                    )}
                  </span>
                </div>
              </Dropdown.Item>
              {index !== options.length - 1 && <Dropdown.Divider />}
            </>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}
